import React, { useState, useContext, useMemo, useRef } from 'react';
import { Input, InputNumber, Select, Tooltip } from 'antd';
import moment from 'moment';
import { TableContext } from './context';

const EditingCell = (props) => {
    const { col, row, rowIndex } = props;
    const { editedValue } = useContext(TableContext);

    const disabled = useMemo(() => (
        typeof col.editable === 'boolean' ?
            col.editable === false : col?.editable?.(row) === false
    ), [col, row]);

    const value = useMemo(() => {
        let text = row[col.dataIndex];
        if (typeof text === 'object' && text) {
            text = JSON.stringify(text);
        }
        return text;
    }, [col, row]);
    const options = useMemo(() => (
        col.rowOptions?.(row) || col.options || []
    ), [col, row]);

    const inputLock = useRef();
    const onChange = (newValue, eventType) => {
        if (eventType === 'compositionstart') {
            inputLock.current = true;
        } else if (eventType === 'compositionend') {
            inputLock.current = false;
        }
        if (inputLock.current) {
            return;
        }
        let formatValue = newValue;
        if (col.type === 'Number') {
            if (newValue === null) {
                formatValue = '';
            }
        } else if (col.type === 'Select') {
            formatValue = !col.multiple ?
                options.find((option) => option.value === newValue)?.label || '' :
                newValue.map((x) => (
                    options.find((option) => option.value === x)?.label || ''
                )).join(col.split || ', ');
            if (!formatValue && col.allowInput) {
                formatValue = newValue;
            }
        } else if (col.type === 'Date') {
            formatValue = moment.isMoment(newValue) ?
                moment(newValue).format('YYYY/MM/DD') : newValue;
        } else if (col.type === 'DateTime') {
            formatValue = moment.isMoment(newValue) ?
                moment(newValue).format('YYYY/MM/DD HH:mm') : newValue;
        } else if (col.type === 'Time') {
            formatValue = moment.isMoment(newValue) ?
                moment(newValue).format('HH:mm') : newValue;
        }
        editedValue.current = { value: newValue, formatValue };
        col.onChange?.(row, newValue);
        const newValidateTitle = col.validate?.(row, newValue);
        if (newValidateTitle) {
            setShowValidateTooltip(true);
            setValidateTitle(newValidateTitle);
        } else {
            setShowValidateTooltip(false);
        }
    };

    const dateRef = useRef();
    const timeout = useRef();
    const [dateText, setDateText] = useState();
    const [dateSelectionRange, setDateSelectionRange] = useState([]);

    const [showValidateTooltip, setShowValidateTooltip] = useState(false);
    const [validateTitle, setValidateTitle] = useState('');

    const content = useMemo(() => {
        let text = row[col.dataIndex];
        if (col.render) {
            text = col.render(text, row, rowIndex);
        } else if (col.filterRender) {
            text = col.filterRender(text, row, rowIndex);
        } else if (typeof text === 'object' && text) {
            text = JSON.stringify(text);
        }
        return text;
    }, [col, row, rowIndex]);

    return (
        <div className="h-full w-full relative">
            <Tooltip
                destroyTooltipOnHide={{ keepParent: false }}
                open={showValidateTooltip}
                placement="bottom"
                title={validateTitle}
                color="#FF6C6C"
                overlayInnerStyle={{ padding: 2, minHeight: 0 }}
            >
                <div className="absolute -top-2 left-1/2 h-full " />
            </Tooltip>
            {(() => {
                switch (col.type) {
                    case 'Text': case 'TextArea': {
                        if (!editedValue.current.value) {
                            editedValue.current = { value };
                        }
                        return (
                            <Input
                                autoFocus
                                bordered={false}
                                disabled={disabled}
                                defaultValue={value}
                                onChange={(e) => onChange(e.target.value, e.type)}
                                onCompositionStart={(e) => onChange(e.target.value, e.type)}
                                onCompositionEnd={(e) => onChange(e.target.value, e.type)}
                                className="px-1"
                            />
                        );
                    }
                    case 'Number': case 'Float': {
                        if (!editedValue.current.value && editedValue.current.value !== 0) {
                            editedValue.current = { value };
                        }
                        return (
                            <InputNumber
                                autoFocus
                                bordered={false}
                                keyboard={false}
                                min={typeof col.min === 'function' ? col.min(row) : col.min ?? 0}
                                max={typeof col.max === 'function' ? col.max(row) : col.max}
                                disabled={disabled}
                                defaultValue={value}
                                onChange={onChange}
                                className="px-1"
                                addonBefore={(() => col.addonBefore?.(row))()}
                            />
                        );
                    }
                    case 'Select': {
                        let defaultValue;
                        if (col.rowValue) {
                            defaultValue = col.rowValue(row);
                        } else {
                            defaultValue = !col.multiple ?
                                options.find((option) => option.label === value)?.value :
                                value?.split(col.split || ', ').map((x) => (
                                    options.find((option) => option.label === x)?.value || ''
                                ));
                        }
                        if (!editedValue.current.value) {
                            editedValue.current = { value: defaultValue };
                        }
                        return (
                            <Select
                                autoFocus
                                bordered={false}
                                size={col.multiple ? 'small' : 'middle'}
                                mode={col.multiple ? 'multiple' : undefined}
                                allowClear={!col.multiple}
                                showSearch={!col.multiple}
                                disabled={disabled}
                                filterOption={(input, data) => {
                                    const targetOption = options.find(
                                        (option) => option.value === data.value
                                    );
                                    return targetOption.label.includes(input);
                                }}
                                defaultValue={defaultValue}
                                onChange={onChange}
                                tagRender={col.multiple ? (option) => {
                                    const currValue = editedValue.current.formatValue || value;
                                    let index;
                                    if (col.rowValue) {
                                        const rowValue = col.rowValue(row);
                                        index = rowValue.findIndex((item) => item === option.value);
                                    } else {
                                        index = currValue?.split(col.split || ', ').findIndex(
                                            (item) => item === option.label
                                        );
                                    }
                                    return index === 0 && (
                                        <div className="-translate-y-[3px]">{currValue}</div>
                                    );
                                } : undefined}
                                onInputKeyDown={(e) => {
                                    setTimeout(() => {
                                        if (col.allowInput) {
                                            if (e.code !== 'Backspace' && !e.target.value) {
                                                return;
                                            }
                                            onChange(e.target.value);
                                        }
                                    });
                                }}
                                className="px-1"
                            >
                                {options.map((option) => (
                                    <Select.Option
                                        value={option.value}
                                        key={option.value}
                                    >
                                        {option?.htmlLabel || option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        );
                    }
                    case 'Date':
                    case 'DateTime': {
                        if (!editedValue.current.value) {
                            editedValue.current = {
                                value: value && moment(value).isValid() ?
                                    moment(value) : ''
                            };
                        }
                        let defaultValue;
                        if (dateText) {
                            defaultValue = dateText;
                        } else if (value && moment(value).isValid()) {
                            defaultValue = value;
                        } else {
                            const year = moment().year();
                            defaultValue = col.type === 'Date' ?
                                `${year}/MM/DD` : `${year}/MM/DD HH:mm`;
                        }
                        setTimeout(() => {
                            if (!dateSelectionRange.length) {
                                let start = defaultValue.search(/[a-zA-Z]/);
                                const end = defaultValue.search(/[a-zA-Z](-|\s|:)/) + 1;
                                if ([1, 2, 3].includes(start)) {
                                    start = 0;
                                } else if ([6, 9, 12, 13].includes(start)) {
                                    start -= 1;
                                }
                                let range;
                                if ([0, 5, 8, 11, 14].includes(start)) {
                                    range = [start, end || defaultValue.length];
                                } else {
                                    range = [defaultValue.length - 2, defaultValue.length];
                                }
                                setDateSelectionRange(range);
                                dateRef.current?.setSelectionRange(...range);
                            } else {
                                dateRef.current?.setSelectionRange(...dateSelectionRange);
                            }
                        });
                        return disabled ? (
                            <div
                                className="h-full w-full flex items-center pl-1
                                    cursor-not-allowed text-[#B2B2B2]"
                            >
                                {value}
                            </div>
                        ) : (
                            <Input
                                ref={dateRef}
                                autoFocus
                                bordered={false}
                                className="px-1"
                                value={defaultValue}
                                onClick={(e) => {
                                    let index = defaultValue.search(/[a-zA-Z]/);
                                    const isStart = [0, 5, 8, 11, 14];
                                    const start = isStart.findLast(
                                        (x) => x <= e.target.selectionStart
                                    );
                                    const curr = isStart.findLast((x) => x <= index);
                                    index = curr ? Math.min(start, curr) : start;
                                    const end = index + (index === 0 ? 4 : 2);
                                    setDateSelectionRange([index, end]);
                                }}
                                onChange={(e) => {
                                    let newDateText;
                                    let newDateSelectionRange = [];
                                    const update = () => {
                                        if (newDateText) {
                                            setDateText(newDateText);
                                            const newValue = moment(newDateText);
                                            if (!/\d/.test(newDateText[0])) {
                                                onChange('');
                                            } else if (newValue.isValid()) {
                                                onChange(newValue);
                                            }
                                        }
                                        setDateSelectionRange(newDateSelectionRange);
                                    };
                                    const isStart = [0, 5, 8, 11, 14];
                                    const startSeq = isStart.findLastIndex(
                                        (x) => x <= e.target.selectionStart
                                    );
                                    if (
                                        e.nativeEvent.inputType === 'insertText' &&
                                        /\d/.test((e.nativeEvent.data))
                                    ) {
                                        clearTimeout(timeout.current);
                                        let index = defaultValue.search(/[a-zA-Z]/);
                                        const currSeq = isStart.findLastIndex((x) => x <= index);
                                        if (currSeq === -1) {
                                            index = Math.min(
                                                isStart[startSeq], dateSelectionRange[0]
                                            );
                                        } else if (startSeq < currSeq) {
                                            index = isStart[startSeq];
                                        }
                                        newDateText = defaultValue.replace(/./g, (c, i) => (
                                            i === index ? e.nativeEvent.data : c
                                        ));
                                        if (
                                            isStart.includes(index) &&
                                            /\d/.test((defaultValue[index]))
                                        ) {
                                            const j = isStart.indexOf(index);
                                            const end = index + (index === 0 ? 4 : 2);
                                            const char = ['Y', 'M', 'D', 'H', 'm'][j];
                                            newDateText = newDateText.replace(/./g, (c, i) => (
                                                i > index && i < end ? char : c
                                            ));
                                        }
                                        let nextStart = isStart.findLast((x) => x <= index + 2);
                                        nextStart = Math.min(
                                            nextStart,
                                            col.type === 'Date' ? 8 : 14
                                        );
                                        const nextEnd = nextStart + (nextStart === 0 ? 4 : 2);
                                        newDateSelectionRange = [nextStart, nextEnd];
                                        update();
                                    } else if (
                                        e.nativeEvent.inputType === 'deleteContentBackward' &&
                                        !e.nativeEvent.data
                                    ) {
                                        timeout.current = setTimeout(() => {
                                            const start = isStart[startSeq];
                                            const end = start + (start === 0 ? 4 : 2);
                                            const deleteText = defaultValue.slice(start, end);
                                            const char = ['Y', 'M', 'D', 'H', 'm'][startSeq];
                                            newDateText = defaultValue.replace(/./g, (c, i) => (
                                                i >= start && i < end ? char : c
                                            ));
                                            if (!/\d/.test(deleteText) && startSeq) {
                                                const nextStart = isStart[startSeq - 1];
                                                const nextEnd = nextStart +
                                                    (nextStart === 0 ? 4 : 2);
                                                newDateSelectionRange = [nextStart, nextEnd];
                                            }
                                            update();
                                        });
                                    } else if (e.nativeEvent.inputType === 'insertCompositionText') {
                                        clearTimeout(timeout.current);
                                        const start = Math.min(
                                            isStart[startSeq], dateSelectionRange[0]
                                        );
                                        const end = start + (start === 0 ? 4 : 2);
                                        newDateSelectionRange = [start, end];
                                        update();
                                    }
                                }}
                            />
                        );
                    }
                    case 'Time': {
                        const timeStr = `${moment().format('YYYY/MM/DD')} ${value}`;
                        if (!editedValue.current.value) {
                            editedValue.current = {
                                value: value && moment(timeStr).isValid() ?
                                    moment(timeStr) : ''
                            };
                        }
                        let defaultValue;
                        if (dateText) {
                            defaultValue = dateText;
                        } else if (value && moment(timeStr).isValid()) {
                            defaultValue = value;
                        } else {
                            defaultValue = 'HH:mm';
                        }
                        setTimeout(() => {
                            if (!dateSelectionRange.length) {
                                let start = defaultValue.search(/[a-zA-Z]/);
                                const end = defaultValue.search(/[a-zA-Z](-|\s|:)/) + 1;
                                if ([1, 4].includes(start)) {
                                    start -= 1;
                                }
                                let range;
                                if ([0, 3].includes(start)) {
                                    range = [start, end || defaultValue.length];
                                } else {
                                    range = [defaultValue.length - 2, defaultValue.length];
                                }
                                setDateSelectionRange(range);
                                dateRef.current?.setSelectionRange(...range);
                            } else {
                                dateRef.current?.setSelectionRange(...dateSelectionRange);
                            }
                        });
                        return disabled ? (
                            <div
                                className="h-full w-full flex items-center pl-1
                                    cursor-not-allowed text-[#B2B2B2]"
                            >
                                {value}
                            </div>
                        ) : (
                            <Input
                                ref={dateRef}
                                autoFocus
                                bordered={false}
                                className="px-1"
                                value={defaultValue}
                                onClick={(e) => {
                                    let index = defaultValue.search(/[a-zA-Z]/);
                                    const isStart = [0, 3];
                                    const start = isStart.findLast(
                                        (x) => x <= e.target.selectionStart
                                    );
                                    const curr = isStart.findLast((x) => x <= index);
                                    index = curr ? Math.min(start, curr) : start;
                                    const end = index + (index === 0 ? 4 : 2);
                                    setDateSelectionRange([index, end]);
                                }}
                                onChange={(e) => {
                                    let newDateText;
                                    let newDateSelectionRange = [];
                                    const update = () => {
                                        if (newDateText) {
                                            setDateText(newDateText);
                                            const str =
                                                `${moment().format('YYYY/MM/DD')}` +
                                                ` ${newDateText}`;
                                            const newValue = moment(str);
                                            if (!/\d/.test(newDateText[0])) {
                                                onChange('');
                                            } else if (newValue.isValid()) {
                                                onChange(newValue.format('HH:mm'));
                                            }
                                        }
                                        setDateSelectionRange(newDateSelectionRange);
                                    };
                                    const isStart = [0, 3];
                                    const startSeq = isStart.findLastIndex(
                                        (x) => x <= e.target.selectionStart
                                    );
                                    if (
                                        e.nativeEvent.inputType === 'insertText' &&
                                        /\d/.test((e.nativeEvent.data))
                                    ) {
                                        clearTimeout(timeout.current);
                                        let index = defaultValue.search(/[a-zA-Z]/);
                                        const currSeq = isStart.findLastIndex((x) => x <= index);
                                        if (currSeq === -1) {
                                            index = Math.min(
                                                isStart[startSeq], dateSelectionRange[0]
                                            );
                                        } else if (startSeq < currSeq) {
                                            index = isStart[startSeq];
                                        }
                                        newDateText = defaultValue.replace(/./g, (c, i) => (
                                            i === index ? e.nativeEvent.data : c
                                        ));
                                        if (
                                            isStart.includes(index) &&
                                            /\d/.test((defaultValue[index]))
                                        ) {
                                            const j = isStart.indexOf(index);
                                            const end = index + 2;
                                            const char = ['H', 'm'][j];
                                            newDateText = newDateText.replace(/./g, (c, i) => (
                                                i > index && i < end ? char : c
                                            ));
                                        }
                                        const nextStart = isStart.findLast((x) => x <= index + 2);
                                        const nextEnd = nextStart + 2;
                                        newDateSelectionRange = [nextStart, nextEnd];
                                        update();
                                    } else if (
                                        e.nativeEvent.inputType === 'deleteContentBackward' &&
                                        !e.nativeEvent.data
                                    ) {
                                        timeout.current = setTimeout(() => {
                                            const start = isStart[startSeq];
                                            const end = start + (start === 0 ? 4 : 2);
                                            const deleteText = defaultValue.slice(start, end);
                                            const char = ['Y', 'M', 'D', 'H', 'm'][startSeq];
                                            newDateText = defaultValue.replace(/./g, (c, i) => (
                                                i >= start && i < end ? char : c
                                            ));
                                            if (!/\d/.test(deleteText) && startSeq) {
                                                const nextStart = isStart[startSeq - 1];
                                                const nextEnd = nextStart +
                                                    (nextStart === 0 ? 4 : 2);
                                                newDateSelectionRange = [nextStart, nextEnd];
                                            }
                                            update();
                                        });
                                    } else if (e.nativeEvent.inputType === 'insertCompositionText') {
                                        clearTimeout(timeout.current);
                                        const start = Math.min(
                                            isStart[startSeq], dateSelectionRange[0]
                                        );
                                        const end = start + (start === 0 ? 4 : 2);
                                        newDateSelectionRange = [start, end];
                                        update();
                                    }
                                }}
                            />
                        );
                    }
                    default: return content;
                }
            })()}
        </div>
    );
};

export default EditingCell;
